import { createContext, useContext, useEffect, useMemo, useState } from "react";
import i18next from "i18next";

// contexts
import { I18nextProvider } from "react-i18next";

// hooks
import { useRouter } from "next/router";
import { useConfig } from "@/contexts/ConfigContext";

// utils
import { parseCookies, setCookie } from "nookies";
import { isSSR } from "@/utils/compute.utils";
import { initI18n } from "@/utils/helpers.utils";

// constants
import { LANGUAGE, STORAGE_KEYS } from "@tajawal/web-core-sdk-v2/lib/constants";

// types
import { Language } from "@tajawal/web-core-sdk-v2/lib/types/common.types";
import type { ReactNode } from "react";
import { AppMiddlewareValues } from "@/types/middleware.types";

interface Localization {
  changeLanguage: (language: Language) => void;
  language: Language;
}

interface ProviderProps {
  children: ReactNode;
  pageProps?: AppMiddlewareValues;
}

const LocalizationContext = createContext<Localization>({} as Localization);

// TODO - Revisit this provider
export function LocalizationProvider({
  children,
  pageProps,
}: ProviderProps): JSX.Element {
  const { locale: translations } = pageProps;
  const defaultLanguage = pageProps?.language?.language ?? LANGUAGE.AR;
  const [language, setLanguage] = useState<Language>(defaultLanguage);
  const { changeLanguage: changeConfigLanguage } = useConfig();
  const { query } = useRouter();

  useMemo(() => initI18n(language, translations), [translations, language]);

  useEffect(() => {
    if (isSSR()) return;

    const queryLang = query.lang as Language;
    const queryLanguage = [LANGUAGE.EN, LANGUAGE.AR].includes(queryLang)
      ? queryLang
      : undefined;
    const cookieLanguage = parseCookies().language as Language;

    // @ts-ignore
    changeLanguage(queryLanguage || cookieLanguage || defaultLanguage);
  }, [query.lang]);

  function changeLanguage(language: Language) {
    const dir = language === LANGUAGE.AR ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
    document.documentElement.className = dir;
    setCookie(null, STORAGE_KEYS.LANGUAGE, language, {
      path: "/",
    });

    changeConfigLanguage(language);
    setLanguage(language);
    i18next.changeLanguage(language);

    if (window && window.config.language !== language) {
      // @ts-ignore
      window.config.language = language;
    }
  }

  return (
    <LocalizationContext.Provider value={{ changeLanguage, language }}>
      <I18nextProvider i18n={i18next}>{children}</I18nextProvider>
    </LocalizationContext.Provider>
  );
}

export function useLocalization(): Localization {
  return useContext<Localization>(LocalizationContext);
}

export default LocalizationContext;
